import React, {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';

export interface GTMTranslations {
  phoneNumber: string;
  text: string;
  additional?: Record<string, string>;
}

interface GTMCallUsBlocksProps {
  defaultTranslations: GTMTranslations;
  directTranslations: GTMTranslations;
  children: (translations: GTMTranslations) => ReactNode;
}

/**
 * Private GTM Call Us Blocks Component
 *
 * Renders the standard GTM call-us structure with both default and direct blocks.
 * This component consolidates the repeated pattern found throughout the website
 * where we have paired gtm-callus-default and gtm-callus-direct divs.
 *
 * The render function approach allows you to write the JSX structure once and
 * have it applied to both blocks with different translation values.
 */
const GTMCallUsBlocks = ({
  defaultTranslations,
  directTranslations,
  children,
}: GTMCallUsBlocksProps) => (
  <>
    {/* Default call-us block (shown by default) */}
    <div className="gtm-callus-default">{children(defaultTranslations)}</div>
    {/* Direct call-us block (GTM can show/hide) */}
    <div className="gtm-callus-direct">{children(directTranslations)}</div>
  </>
);

interface GTMCallUsProps {
  defaultKeys: {
    phoneNumber: string;
    text: string;
    additional?: Record<string, string>;
  };
  directKeys: {
    phoneNumber: string;
    text: string;
    additional?: Record<string, string>;
  };
  namespace: string;
  children: (translations: GTMTranslations) => ReactNode;
}

/**
 * GTM Call Us Component
 *
 * Renders GTM call-us blocks with translation key-based configuration.
 * Pass translation keys instead of translated values for better performance
 * and cleaner code.
 *
 * @param defaultKeys - Translation keys for the default block
 * @param directKeys - Translation keys for the direct block
 * @param namespace - i18next namespace to use for translations
 * @param children - Render function that receives translations and returns JSX
 */
export const GTMCallUs = ({
  defaultKeys,
  directKeys,
  namespace,
  children,
}: GTMCallUsProps) => {
  const {t} = useTranslation(namespace);

  // Helper function to translate additional keys
  const translateAdditional = (additional?: Record<string, string>) => {
    if (!additional) return undefined;
    const result: Record<string, string> = {};
    for (const [key, translationKey] of Object.entries(additional)) {
      result[key] = t(translationKey);
    }
    return result;
  };

  const defaultTranslations: GTMTranslations = {
    additional: translateAdditional(defaultKeys.additional),
    phoneNumber: t(defaultKeys.phoneNumber),
    text: t(defaultKeys.text),
  };

  const directTranslations: GTMTranslations = {
    additional: translateAdditional(directKeys.additional),
    phoneNumber: t(directKeys.phoneNumber),
    text: t(directKeys.text),
  };

  return (
    <GTMCallUsBlocks
      defaultTranslations={defaultTranslations}
      directTranslations={directTranslations}>
      {children}
    </GTMCallUsBlocks>
  );
};
